import { UiSchema } from '@rjsf/utils';
import { KioskStringsDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { KioskSettingsForm } from './form';
import { CustomObjectFieldTemplate, UICollapsibleSection, UIGridElement } from '../shared/custom-object-field-template';
const onScreenTextSection: UICollapsibleSection = {
  id: 'customise-on-screen-text',
  title: 'Customise on-screen text',
  defaultExpanded: false
};

/** Fields a client kiosk inherits from its manager; shown read-only on the client form. */
const CLIENT_INHERITED_FIELDS: (keyof KioskSettingsForm)[] = [
  'countryCode',
  'scannerMode',
  'cameraEnabled',
  'cameraDriver',
  'cameraDirection',
  'cameraRotation',
  'externalFlow',
  'internalUserSingular',
  'internalUserPlural',
  'externalUserSingular',
  'externalUserPlural',
  'scanQrCodeHeading',
  'scanQrCodeSubheading',
  'externalLoginPhoneLoginHeading',
  'externalLoginPhoneLoginSubheading',
  'collectionCodeSubHeading',
  'noDoorsAvailableSubheading',
  'csPhoneInternal',
  'csPhoneExternal',
  'groundfloorPortalAddress'
];

export interface KioskSettingsFormUISchemaOptions {
  /** Kiosk name is hidden for single-sided kiosks. */
  showKioskName: boolean;
  /** Backend default strings, shown as placeholders; blank fields fall back to these on the kiosk. */
  stringDefaults?: KioskStringsDto;
  /** Client kiosks drop the IoT Hub ID and locker type, and only name, mode and home screen buttons are editable. */
  isClient?: boolean;
}

function generalFields(options: KioskSettingsFormUISchemaOptions): UIGridElement['fields'] {
  if (options.isClient) return options.showKioskName ? { mode: 6, name: 6 } : { mode: 6 };
  return options.showKioskName ? { iotHubId: 4, mode: 4, name: 4 } : { iotHubId: 6, mode: 6 };
}

export function kioskSettingsFormUISchema(options: KioskSettingsFormUISchemaOptions): UiSchema {
  const uiGrid: UIGridElement[] = [
    {
      title: 'General',
      ...(options.isClient && {
        notice: {
          // The template hides notices without a predicate.
          isEnabledPredicate: () => true,
          notice: 'Disabled fields are inherited from the manager kiosk. Edit them on the manager.',
          severity: 'info' as const
        }
      }),
      fields: generalFields(options)
    },
    { fields: { countryCode: 6 } },
    { title: 'Hardware', fields: options.isClient ? { scannerMode: 6 } : { lockerType: 6, scannerMode: 6 } },
    { fields: { cameraEnabled: 3, cameraDriver: 3, cameraDirection: 3, cameraRotation: 3 } },
    {
      title: 'User interface',
      subheading: 'Internal (Residents)',
      fields: { showInternalUserButton: 6, internalUserButtonText: 6 }
    },
    {
      subheading: 'External (Couriers/Team Members)',
      fields: { showExternalUserButton: 4, externalUserButtonText: 4, externalFlow: 4 }
    },
    // Single-line fields are paired; multi-line text areas get their own rows so field heights line up.
    { section: onScreenTextSection, fields: { internalUserSingular: 6, internalUserPlural: 6 } },
    { section: onScreenTextSection, fields: { externalUserSingular: 6, externalUserPlural: 6 } },
    { section: onScreenTextSection, fields: { scanQrCodeHeading: 6, scanQrCodeSubheading: 6 } },
    { section: onScreenTextSection, fields: { externalLoginPhoneLoginHeading: 12 } },
    { section: onScreenTextSection, fields: { externalLoginPhoneLoginSubheading: 12 } },
    { section: onScreenTextSection, fields: { collectionCodeSubHeading: 6, noDoorsAvailableSubheading: 6 } },
    { section: onScreenTextSection, fields: { csPhoneInternal: 6, csPhoneExternal: 6 } },
    { section: onScreenTextSection, fields: { groundfloorPortalAddress: 12 } }
  ];

  const uiSchema: UiSchema = {
    'ui:submitButtonOptions': { norender: true },
    'ui:field': 'custom',
    'ui:grid': uiGrid,
    'ui:ObjectFieldTemplate': CustomObjectFieldTemplate,
    // Booleans render as checkboxes by default; force the Enabled/Disabled dropdown.
    cameraEnabled: { 'ui:widget': 'select' },
    externalLoginPhoneLoginSubheading: {
      'ui:widget': 'textarea',
      'ui:options': { rows: 2 }
    },
    collectionCodeSubHeading: {
      'ui:widget': 'textarea',
      'ui:options': { rows: 2 }
    },
    noDoorsAvailableSubheading: {
      'ui:widget': 'textarea',
      'ui:options': { rows: 2 }
    }
  };

  for (const [field, value] of Object.entries(options.stringDefaults ?? {})) {
    if (!value) continue;
    uiSchema[field] = { ...uiSchema[field], 'ui:placeholder': value };
  }

  if (options.isClient) {
    for (const field of CLIENT_INHERITED_FIELDS) {
      uiSchema[field] = { ...uiSchema[field], 'ui:readonly': true };
    }
  }

  return uiSchema;
}
